import { TTicketDetails } from '@daymade/api-contracts';
import { EntriesAndStats } from '../../components/dashboard/EntriesAndStats/EntriesAndStats';

// Minimal valid dummy ticket data for preview
import {
  DrawOrderStatus,
  GameType,
  OrderStatus,
  PaymentMethod,
  SubscriptionStatus,
} from '@daymade/common-lib/dbEnums';
import AboutUsSiteInnerContainer from 'components/about-us/AboutUsSiteInnerContainer';
import { TSubscriptions } from 'stores/UserStore';
import { IEntryLine } from 'types/Draws';

const dummyEntry: IEntryLine = {
  type: GameType.LOTTERY,
  main: [1, 2, 3, 4],
  bonus: [6],
};

const dummyTickets: TTicketDetails[] = [
  {
    entries: [dummyEntry, dummyEntry, dummyEntry],
    paymentRequest: { purchasingAction: 'SUBSCRIPTION' },
  } as any,
];

const dummyAddOnTickets: TTicketDetails[] = [
  {
    entries: [dummyEntry],
    paymentRequest: { purchasingAction: 'PROMO' },
  } as any,
];

const emptyTickets: TTicketDetails[] = [];

const dummyActiveSubscription: TSubscriptions[0] = {
  order: {
    id: 'c142b909-3d3d-4857-a417-d874557f09f7',
    createdAt: new Date('2026-03-18T10:24:47.645Z'),
    status: OrderStatus.COMPLETED,
    playerId: '5bbe8378-82a1-45c4-a880-efd244571e82',
    isSubscription: true,
    totalFiatAmount: 295,
    tokenAmount: 0,
    paymentMethod: PaymentMethod.ADYEN,
    subscriptionId: '7ff338c9-b70d-4038-a645-2f1b425cbb8b',
  },
  drawOrder: {
    id: 'd4e0051b-1754-4fd1-b055-7d7ec6e4d6dc',
    status: DrawOrderStatus.ACTIVE,
    orderId: 'c142b909-3d3d-4857-a417-d874557f09f7',
    playerId: '5bbe8378-82a1-45c4-a880-efd244571e82',
    firstDrawId: 9778,
    drawTypeCode: 'DAYMADEDRAW',
    entries: [
      {
        type: GameType.LOTTERY,
        main: [6, 40, 46, 50],
        bonus: [7],
      },
    ],
    price: 295,
    createdAt: new Date('2026-03-18T10:24:47.645Z'),
  },
  subscription: {
    id: '7ff338c9-b70d-4038-a645-2f1b425cbb8b',
    status: SubscriptionStatus.ACTIVE,
    playerId: '5bbe8378-82a1-45c4-a880-efd244571e82',
    orderId: 'c142b909-3d3d-4857-a417-d874557f09f7',
    paymentDow: 3,
    failedAt: null,
    version: 1,
    createdAt: new Date('2026-03-18T10:24:52.592Z'),
    nextPaymentDate: new Date('2026-03-25T02:00:00.000Z'),
    cancelledAt: new Date('2026-03-19T10:54:33.256Z'),
    snoozedUntil: null,
  },
  drawSchedulePlans: [
    {
      drawSchedule: {
        id: 1,
        templateName: 'Weekly Tuesday Draw',
        description: null,
        code: 'TUESDAY',
        drawTypeCode: 'DAYMADEDRAW',
        prizeStructureId: 1,
      },
      drawTypePlan: {
        id: 1,
        drawTypeCode: 'DAYMADEDRAW',
        code: 'PREMIUM',
        subcode: 'BASE',
        name: 'Premium Tickets',
        desc: null,
        priceVersion: 0,
        priceTable: {
          '0': {
            GBP: {
              '1': 295,
              '2': 275,
              '3': 260,
              '4': 250,
            },
            TOKEN: {
              '1': 6,
            },
          },
        },
      },
    },
  ],
  drawType: {
    code: 'DAYMADEDRAW',
    name: 'Daymade Main Draw',
    gameType: GameType.LOTTERY,
    group: 'DAYMADEDRAW',
    numberSetup: {
      type: GameType.LOTTERY,
      main: [55, 55, 55, 55],
      bonus: [10],
    },
  },
};

const upcomingDrawDate = new Date().getTime() + 1000 * 60 * 60 * 24 * 5; // 5 days from now

const activeSubscriberWithEntriesProps = {
  isFtmp: false,
  tickets: dummyTickets,
  tokenBalance: 10,
  leaderboardPoints: 100,
  activeSubscription: dummyActiveSubscription,
};

const activeSubscriberWithAddOnEntriesProps = {
  isFtmp: false,
  tickets: [...dummyTickets, ...dummyAddOnTickets],
  tokenBalance: 10,
  leaderboardPoints: 100,
  activeSubscription: dummyActiveSubscription,
};

const activeSubscriberWithNoEntriesProps = {
  isFtmp: false,
  tickets: emptyTickets,
  tokenBalance: 10,
  leaderboardPoints: 100,
  activeSubscription: dummyActiveSubscription,
};

const cancelledSubscriberWithEntriesProps = {
  isFtmp: false,
  tickets: dummyTickets,
  tokenBalance: 10,
  leaderboardPoints: 100,
  activeSubscription: undefined,
};

const cancelledSubscriberNoEntriesProps = {
  isFtmp: false,
  tickets: emptyTickets,
  tokenBalance: 10,
  leaderboardPoints: 100,
  activeSubscription: undefined,
};

const ftmpNoSubscriptionNoEntriesProps = {
  isFtmp: true,
  tickets: emptyTickets,
  tokenBalance: 10,
  leaderboardPoints: 100,
  activeSubscription: undefined,
};

const PREVIEWS = [
  {
    label: 'ACTIVE subscriber with entries',
    props: activeSubscriberWithEntriesProps,
  },
  {
    label: 'ACTIVE subscriber with add-on entries',
    props: activeSubscriberWithAddOnEntriesProps,
  },
  {
    label: 'ACTIVE subscriber with no entries',
    props: activeSubscriberWithNoEntriesProps,
  },
  {
    label: 'CANCELLED subscriber with entries',
    props: cancelledSubscriberWithEntriesProps,
  },
  {
    label: 'CANCELLED subscriber with no entries',
    props: cancelledSubscriberNoEntriesProps,
  },
  {
    label: 'FTMP no subscription, no entries',
    props: ftmpNoSubscriptionNoEntriesProps,
  },
];

const EntriesAndStatsPreview = () => (
  <div
    style={{
      display: 'flex',
      padding: 16,
      flexDirection: 'column',
      gap: 16,
      background: '#f6f6f6',
    }}
  >
    <h2 className="aboutUsTypographyHeaderLarge">
      Dashboard Entries Component
    </h2>
    {PREVIEWS.map(({ label, props }) => (
      <div key={label}>
        <h4
          className="aboutUsTypographyHeaderMedium"
          style={{ marginBottom: 15 }}
        >
          {label}
        </h4>
        <AboutUsSiteInnerContainer background="white" borderRadius withPadding>
          <EntriesAndStats
            {...props}
            upcomingDrawDate={new Date(upcomingDrawDate)}
          />
        </AboutUsSiteInnerContainer>
      </div>
    ))}
  </div>
);

EntriesAndStatsPreview.daymadeDesignSystemInfo = {
  name: 'Entries And Stats',
};

export default EntriesAndStatsPreview;
